/**
 * Deep Link Utilities
 *
 * URL construction and parsing for comment deep linking.
 * Uses hash fragments to avoid server requests and CMS conflicts.
 */

const HASH_PREFIX = '#collab-hub-comment=';

/**
 * Get comment ID from URL hash if present.
 * Returns null if hash doesn't match expected format.
 */
export function getCommentIdFromHash(): string | null {
  const hash = window.location.hash;
  if (hash.startsWith(HASH_PREFIX)) {
    return hash.slice(HASH_PREFIX.length) || null;
  }
  return null;
}

/**
 * Construct a deep link URL for a comment.
 * Uses current page URL with comment hash fragment.
 */
export function getCommentDeepLink(commentId: string): string {
  const baseUrl = window.location.origin + window.location.pathname;
  return `${baseUrl}${HASH_PREFIX}${commentId}`;
}

/**
 * Clear the comment hash from URL without triggering navigation.
 * Preserves any existing query parameters.
 */
export function clearCommentHash(): void {
  const url =
    window.location.origin + window.location.pathname + window.location.search;
  history.replaceState(null, '', url);
}

/**
 * Copy text to clipboard with fallback for older browsers.
 * Returns true on success, false on failure.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (error) {
    console.error('[DeepLink] Copy to clipboard failed:', error);
    return false;
  }
}
